/**
 * Watches an element above a list layout and keeps `--grid-height` on the target element
 * sized to fill the remaining viewport below `observedElement`, minus `listHeaderHeight`.
 *
 * Intended for desktop layouts where a detail card sits above a `tm-list-layout` and the
 * vaadin-grid inside must fill the remaining space without creating an outer scrollbar.
 *
 * @param observedElement - The element whose bottom edge marks where the grid area begins
 *   (e.g. an account-detail-graph above the list).
 * @param targetElement - The element to set `--grid-height` on (e.g. tm-list-layout).
 * @param listHeaderHeight - Height in px of any fixed header rendered inside `targetElement`
 *   above the grid (e.g. a tm-list-header). Defaults to 80.
 * @returns An object with an `unsubscribe` function that disconnects the observer and removes
 *   the window resize listener.
 */
export function watchGridHeight(
    observedElement: HTMLElement,
    targetElement: HTMLElement,
    listHeaderHeight = 80
): { unsubscribe: () => void } {
    const update = () => {
        const gridHeight = Math.max(
            window.innerHeight - observedElement.getBoundingClientRect().bottom - listHeaderHeight,
            200
        );
        targetElement.style.setProperty('--grid-height', `${gridHeight}px`);
    };

    const observer = new ResizeObserver(update);
    observer.observe(observedElement);
    window.addEventListener('resize', update);
    update();

    return {
        unsubscribe: () => {
            observer.disconnect();
            window.removeEventListener('resize', update);
        },
    };
}
